import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPen, FaRegTrashAlt } from 'react-icons/fa';
import dayjs, { Dayjs } from 'dayjs';

import {
  PermissionKeys,
  PortfolioTenantPayload,
  PortfolioTenantReadModel,
  TenantReadModel,
  useDeleteSpacesByIdPortfolioTenantsAndTenantIdMutation,
  useGetSpacesByIdPortfolioTenantsQuery,
  usePostSpacesByIdPortfolioTenantsMutation,
  usePutSpacesByIdPortfolioTenantsAndTenantIdMutation,
} from '@client/api';
import { useSpacePermissions } from '../../../../hooks/useSpacePermissions/useSpacePermissions';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  DatepickerV2,
  EmptyPlaceholder,
  Input,
  Modal,
  ModalFooter,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  toCurrencyString,
} from '@client/toolkit';
import type { CurrencyCode } from '@client/toolkit';
import { PortfolioTenantSelectionModal } from './portfolio-tenant-selection-modal.component';
import { DeleteConfirmationModalContent } from '../../../../components/delete-confirmation-modal-content/delete-confirmation-modal-content';

export interface PortfolioTenantsCardProps {
  spaceId?: string;
  buildingId?: string;
}

interface InlineEditState {
  tenantId: string | null; // null = new row
  companyId: string | null;
  companyName: string;
  status: 'Rented' | 'Vacant';
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  yearlyIncome: string;
}

const emptyEdit = (): InlineEditState => ({
  tenantId: null,
  companyId: null,
  companyName: '',
  status: 'Rented',
  fromDate: null,
  toDate: null,
  yearlyIncome: '',
});

const fromTenant = (t: PortfolioTenantReadModel): InlineEditState => ({
  tenantId: t.id,
  companyId: t.companyId ?? null,
  companyName: t.companyName ?? '',
  status: t.status,
  fromDate: t.fromDate ? dayjs(t.fromDate) : null,
  toDate: t.toDate ? dayjs(t.toDate) : null,
  yearlyIncome: t.yearlyIncome?.toString() ?? '',
});

export function PortfolioTenantsCard({ spaceId, buildingId }: PortfolioTenantsCardProps) {
  const { t: tCommon } = useTranslation();

  const { hasAnyPermission } = useSpacePermissions({ id: spaceId ?? buildingId });
  const canEdit = hasAnyPermission([PermissionKeys.Space.Update]);

  const { data: tenants, isLoading } = useGetSpacesByIdPortfolioTenantsQuery({ id: spaceId ?? '' }, { skip: !spaceId });

  const [createTenant, { isLoading: isCreating }] = usePostSpacesByIdPortfolioTenantsMutation();
  const [updateTenant, { isLoading: isUpdating }] = usePutSpacesByIdPortfolioTenantsAndTenantIdMutation();
  const [deleteTenant, { isLoading: isDeleting }] = useDeleteSpacesByIdPortfolioTenantsAndTenantIdMutation();

  const isSaving = isCreating || isUpdating;

  // Editing mode (top-level toggle like Tilleggsprodukter)
  const [editing, setEditing] = useState(false);

  // Inline editing state for a single row
  const [editRow, setEditRow] = useState<InlineEditState | null>(null);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);

  // Delete confirmation state
  const [deletingTenant, setDeletingTenant] = useState<PortfolioTenantReadModel | null>(null);

  const handleOpenEdit = () => {
    setEditing(true);
  };

  const handleAdd = () => {
    setEditRow(emptyEdit());
    setSelectionModalOpen(true);
  };

  const handleEditRow = (tenant: PortfolioTenantReadModel) => {
    setEditRow(fromTenant(tenant));
  };

  const handleCancel = () => {
    setEditRow(null);
    setEditing(false);
  };

  const handleCancelRow = () => {
    setEditRow(null);
  };

  const handleSave = async () => {
    if (!spaceId || !editRow) return;

    const payload: PortfolioTenantPayload = {
      companyName: editRow.companyName || null,
      companyId: editRow.companyId ?? null,
      status: editRow.status,
      fromDate: editRow.fromDate?.format('YYYY-MM-DD') ?? '',
      toDate: editRow.toDate?.format('YYYY-MM-DD') ?? null,
      yearlyIncome: parseFloat(editRow.yearlyIncome) || 0,
    };

    try {
      if (editRow.tenantId) {
        await updateTenant({ id: spaceId, tenantId: editRow.tenantId, body: payload }).unwrap();
      } else {
        await createTenant({ id: spaceId, body: payload }).unwrap();
      }
      setEditRow(null);
    } catch {
      // error handled by RTK
    }
  };

  const handleCompanySelected = (items: TenantReadModel[]) => {
    if (items.length > 0 && editRow) {
      const company = items[0];
      setEditRow({
        ...editRow,
        companyId: company.companyId ?? null,
        companyName: company.name ?? '',
      });
    }
    setSelectionModalOpen(false);
  };

  const handleOpenCompanySearch = () => {
    setSelectionModalOpen(true);
  };

  // Delete confirmation
  const handleDeleteRequest = (tenant: PortfolioTenantReadModel) => {
    setDeletingTenant(tenant);
  };

  const handleDeleteConfirm = async () => {
    if (!spaceId || !deletingTenant) return;
    try {
      await deleteTenant({ id: spaceId, tenantId: deletingTenant.id }).unwrap();
    } catch {
      // error handled by RTK
    }
    setDeletingTenant(null);
  };

  const handleDeleteCancel = () => {
    setDeletingTenant(null);
  };

  const formatCurrency = (amount: number, currency: string) =>
    toCurrencyString(amount, '\u2013', { currency: currency as CurrencyCode });

  const isEditingRow = (tenant: PortfolioTenantReadModel) => editRow !== null && editRow.tenantId === tenant.id;

  const canSave = editRow !== null && editRow.companyName.trim().length > 0 && editRow.fromDate !== null;

  const isListEmpty = !tenants?.length && !editing;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex-1" variant="highlighted">
            Leietakere
          </CardTitle>
          <CardActions>
            {!editing ? (
              canEdit && (
                <Button size="sm" onClick={handleOpenEdit}>
                  {tCommon('edit')}
                </Button>
              )
            ) : (
              <>
                <Button size="sm" color="primary-dark" onClick={handleCancel} disabled={isSaving}>
                  {tCommon('cancel')}
                </Button>
                <Button size="sm" color="primary-dark" onClick={handleAdd} disabled={isSaving}>
                  {tCommon('add')}
                </Button>
                <Button size="sm" color="primary" onClick={handleSave} disabled={isSaving || !canSave}>
                  {isSaving ? 'Lagrer...' : tCommon('save')}
                </Button>
              </>
            )}
          </CardActions>
        </CardHeader>

        <CardContent variant="fill" className="justify-self-center rounded-lg w-full px-4 pb-4">
          <Table className="overflow-hidden w-full mb-4 rounded-md" size="xs" layout="fixed">
            {!isLoading && isListEmpty && (
              <caption className="px-4 py-4 [caption-side:bottom]">
                <EmptyPlaceholder>{tCommon('noEntityFound', { entity: 'leietakere' })}</EmptyPlaceholder>
              </caption>
            )}

            <TableHead>
              <TableHeadRow>
                <TableHeadCell className="w-[22%]">Selskap</TableHeadCell>
                <TableHeadCell className="w-[10%]">Status</TableHeadCell>
                <TableHeadCell className="w-[14%]">Fra dato</TableHeadCell>
                <TableHeadCell className="w-[14%]">Til dato</TableHeadCell>
                <TableHeadCell className="w-[14%]" align="right">
                  Årlig inntekt
                </TableHeadCell>
              </TableHeadRow>
            </TableHead>

            <TableBody>
              {isLoading &&
                Array.from(Array(3).keys()).map((x) => (
                  <TableRow key={x}>
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                  </TableRow>
                ))}

              {/* View mode rows */}
              {!isLoading &&
                !editing &&
                tenants?.map((tenant) => (
                  <TableRow key={tenant.id} verticalAlign="middle">
                    <TableCell>
                      <strong>{tenant.companyName ?? '–'}</strong>
                    </TableCell>
                    <TableCell>
                      <span className={tenant.status === 'Rented' ? 'text-green-400' : 'text-yellow-400'}>
                        {tenant.status === 'Rented' ? 'Utleid' : 'Ledig'}
                      </span>
                    </TableCell>
                    <TableCell>{tenant.fromDate ? dayjs(tenant.fromDate).format('DD.MM.YYYY') : '–'}</TableCell>
                    <TableCell>{tenant.toDate ? dayjs(tenant.toDate).format('DD.MM.YYYY') : '–'}</TableCell>
                    <TableCell align="right">{formatCurrency(tenant.yearlyIncome, tenant.currency)}</TableCell>
                  </TableRow>
                ))}

              {/* Edit mode rows */}
              {!isLoading &&
                editing &&
                tenants?.map((tenant) =>
                  isEditingRow(tenant) ? (
                    <InlineEditRow
                      key={tenant.id}
                      editRow={editRow!}
                      setEditRow={setEditRow}
                      onOpenCompanySearch={handleOpenCompanySearch}
                      onDelete={() => handleDeleteRequest(tenant)}
                    />
                  ) : (
                    <TableRow key={tenant.id} verticalAlign="middle">
                      <TableCell>
                        <strong>{tenant.companyName ?? '–'}</strong>
                      </TableCell>
                      <TableCell>
                        <span className={tenant.status === 'Rented' ? 'text-green-400' : 'text-yellow-400'}>
                          {tenant.status === 'Rented' ? 'Utleid' : 'Ledig'}
                        </span>
                      </TableCell>
                      <TableCell>{tenant.fromDate ? dayjs(tenant.fromDate).format('DD.MM.YYYY') : '–'}</TableCell>
                      <TableCell>{tenant.toDate ? dayjs(tenant.toDate).format('DD.MM.YYYY') : '–'}</TableCell>
                      <TableCell align="right" className="flex items-center justify-end">
                        <div className="whitespace-nowrap">{formatCurrency(tenant.yearlyIncome, tenant.currency)}</div>
                        <Button
                          className="ml-2 text-brand-primary bg-surface-elevated"
                          size="xs"
                          color="custom"
                          onClick={() => handleEditRow(tenant)}
                        >
                          <FaPen />
                        </Button>
                        <Button
                          className="ml-1 text-brand-primary bg-surface-elevated"
                          size="xs"
                          color="custom"
                          onClick={() => handleDeleteRequest(tenant)}
                        >
                          <FaRegTrashAlt />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ),
                )}

              {/* New row (when adding) */}
              {editing && editRow !== null && editRow.tenantId === null && (
                <InlineEditRow
                  editRow={editRow}
                  setEditRow={setEditRow}
                  onOpenCompanySearch={handleOpenCompanySearch}
                  onDelete={handleCancelRow}
                />
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PortfolioTenantSelectionModal
        open={selectionModalOpen}
        buildingId={buildingId}
        onClose={() => setSelectionModalOpen(false)}
        onSubmit={handleCompanySelected}
      />

      {/* Delete confirmation modal */}
      <Modal
        open={deletingTenant !== null}
        onClose={handleDeleteCancel}
        onBackdropClick={handleDeleteCancel}
        closeDisabled={isDeleting}
      >
        <DeleteConfirmationModalContent name={deletingTenant?.companyName ?? 'leietaker'} />
        <ModalFooter className="justify-center">
          <Button color="danger" onClick={handleDeleteConfirm} progressing={isDeleting}>
            {tCommon('delete')}
          </Button>
          <Button color="primary-dark" onClick={handleDeleteCancel}>
            {tCommon('cancel')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Inline edit row
// ---------------------------------------------------------------------------

interface InlineEditRowProps {
  editRow: InlineEditState;
  setEditRow: (state: InlineEditState) => void;
  onOpenCompanySearch: () => void;
  onDelete?: () => void;
}

function InlineEditRow({ editRow, setEditRow, onOpenCompanySearch, onDelete }: InlineEditRowProps) {
  return (
    <TableRow verticalAlign="middle">
      <TableCell>
        <Button size="sm" color="primary-dark" className="w-full text-left truncate" onClick={onOpenCompanySearch}>
          {editRow.companyName || 'Velg selskap...'}
        </Button>
      </TableCell>
      <TableCell>
        <Select
          size="medium"
          value={editRow.status}
          onChange={(e) => setEditRow({ ...editRow, status: e.target.value as 'Rented' | 'Vacant' })}
        >
          <option value="Rented">Utleid</option>
          <option value="Vacant">Ledig</option>
        </Select>
      </TableCell>
      <TableCell>
        <DatepickerV2
          value={editRow.fromDate ?? undefined}
          onChange={(date) => setEditRow({ ...editRow, fromDate: date ?? null })}
          className="w-full"
        />
      </TableCell>
      <TableCell>
        <DatepickerV2
          value={editRow.toDate ?? undefined}
          onChange={(date) => setEditRow({ ...editRow, toDate: date ?? null })}
          className="w-full"
        />
      </TableCell>
      <TableCell align="right" className="flex items-center justify-end">
        <Input
          type="number"
          size="medium"
          value={editRow.yearlyIncome}
          onChange={(e) => setEditRow({ ...editRow, yearlyIncome: e.target.value })}
          placeholder="0"
        />
        {onDelete && (
          <Button className="ml-2 text-brand-primary bg-surface-elevated" size="xs" color="custom" onClick={onDelete}>
            <FaRegTrashAlt />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
